<template>
    <!--
        Day calendar for DateRangePicker. In range mode two clicks pick a start
        and an end; in single mode one click picks one day. Either way it emits
        `select` with { start, end } as luxon DateTimes (equal in single mode).
    -->
    <div    class="drp-calendar-pane">
        <vue-datepicker inline
                        :range="isRange"
                        auto-apply
                        :config="{ keepViewOnOffsetClick: true }"
                        :day-class="dayClass"
                        :enable-time-picker="false"
                        :max-date="maxDate ? maxDate.toJSDate() : null"
                        :model-value="pickerValue"
                        :month-change-on-scroll="false"
                        no-today
                        :start-date="(end ?? today).toJSDate()"
                        focus-start-date
                        :week-start="0"
                        @update:model-value="onUpdate"
        >
            <template #month-year="{ month, year, handleMonthYearChange }">
                <date-range-nav class="mb-2"
                                :title="monthTitle(year, month + 1)"
                                :next-disabled="isAfterMax(year, month + 2)"
                                @prev="handleMonthYearChange(false, true)"
                                @next="handleMonthYearChange(true, true)"
                ></date-range-nav>
            </template>
        </vue-datepicker>
    </div>
</template>

<script>
import VueDatepicker from '@vuepic/vue-datepicker'
import { DateTime } from 'luxon'
import DateRangeNav from './DateRangeNav.vue'

export default {
    name: 'DateRangeCalendarPane',
    components: { DateRangeNav, VueDatepicker },
    emits: ['select'],
    props: {
        mode: {
            type: String,
            default: 'range',
        },
        // luxon DateTimes; null when nothing is selected yet.
        start: {
            type: Object,
            default: null,
        },
        end: {
            type: Object,
            default: null,
        },
        today: {
            type: Object,
            required: true,
        },
        // Last selectable day, or null for no limit.
        maxDate: {
            type: Object,
            default: null,
        },
    },
    computed: {
        isRange() {
            return this.mode !== 'single'
        },
        pickerValue() {
            if (!this.start) {
                return null
            }
            if (!this.isRange) {
                return this.start.toJSDate()
            }
            return [this.start.toJSDate(), (this.end ?? this.start).toJSDate()]
        },
    },
    methods: {
        onUpdate(value) {
            if (!this.isRange) {
                if (!(value instanceof Date)) {
                    return
                }
                const day = DateTime.fromJSDate(value).startOf('day')
                this.$emit('select', { start: day, end: day })
                return
            }
            // The picker also emits after the first click of a range, with no
            // end yet; wait for the second click (the same day twice picks one day).
            if (!Array.isArray(value) || !value[0] || !value[1]) {
                return
            }
            this.$emit('select', {
                start: DateTime.fromJSDate(value[0]).startOf('day'),
                end: DateTime.fromJSDate(value[1]).startOf('day'),
            })
        },
        // The picker's own today marker follows the browser's date, so it is
        // turned off (no-today) and the zone's today is marked here instead.
        dayClass(date) {
            return DateTime.fromJSDate(date).hasSame(this.today, 'day') ? 'drp-today' : ''
        },
        // True when the given month (1-12, may overflow to 13) starts after maxDate.
        isAfterMax(year, month) {
            if (!this.maxDate) {
                return false
            }
            return DateTime.fromObject({ year, month: 1 }).plus({ months: month - 1 }) > this.maxDate
        },
        monthTitle(year, month) {
            return DateTime.fromObject({ year, month }).toFormat('LLLL yyyy')
        },
    },
}
</script>

<style scoped>
/* The picker sizes its menu to --dp-menu-min-width (260px); stretch it so the
   calendar fills the pane, however wide the page makes it. The theme class
   sits on both the root and the menu, so set the variable on each. */
.drp-calendar-pane :deep(.dp__theme_light) {
    --dp-menu-min-width: 100%;
}

.drp-calendar-pane :deep(.dp__main > div:has(.dp__outer_menu_wrap)) {
    flex: 1;
}

.drp-calendar-pane :deep(.dp__calendar_header_separator) {
    display: none;
}

.drp-calendar-pane :deep(.dp__calendar_header_item) {
    color: #6b7280;
    font-weight: 400;
}

/* Full-width cells so the range shading reads as one continuous band. */
.drp-calendar-pane :deep(.dp__calendar_item) {
    flex: 1;
}

.drp-calendar-pane :deep(.dp__cell_inner) {
    width: 100%;
}

/* A one-day range is both the start and the end, and each class squares off
   one side; keep the single selected day fully rounded. */
.drp-calendar-pane :deep(.dp__range_start.dp__range_end) {
    border-radius: var(--dp-cell-border-radius);
}

.drp-calendar-pane :deep(.dp__cell_offset) {
    color: #6b7280;
}

/* Days from the next or previous month can be selected too; keep their text
   white on the blue fill instead of the gray offset color. */
.drp-calendar-pane :deep(.dp__cell_offset.dp__range_start),
.drp-calendar-pane :deep(.dp__cell_offset.dp__range_end),
.drp-calendar-pane :deep(.dp__cell_offset.dp__active_date) {
    color: var(--dp-primary-text-color);
}

.drp-calendar-pane :deep(.drp-today:not(.dp__active_date):not(.dp__range_start):not(.dp__range_end)) {
    border-color: #f59e0b;
}
</style>
